export interface DocumentationPhoto {
  uuid: string;
  altText: string;
  imageUrl: string;
  position?: number;
}

export interface DocumentationActivity {
  uuid: string;
  slug: string;
  title: string;
}

export interface PublicDocumentation {
  uuid: string;
  title: string;
  description: string;
  documentationDate: string;
  documentationLocation: string;
  photos: DocumentationPhoto[];
  activity: DocumentationActivity | null;
}

export interface DocumentationMeta {
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

export interface DocumentationResponse {
  data?: unknown;
  meta?: Partial<DocumentationMeta> | null;
}

export interface DocumentationFilters {
  page: number;
  size: number;
  year?: string;
  activitySlug?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const getDocumentationRows = (
  response: DocumentationResponse,
): PublicDocumentation[] =>
  Array.isArray(response.data) ? (response.data as PublicDocumentation[]) : [];

export const getDocumentationDetail = (
  response: DocumentationResponse,
): PublicDocumentation | null =>
  isRecord(response.data) && !Array.isArray(response.data)
    ? (response.data as unknown as PublicDocumentation)
    : null;

export const getDocumentationMeta = (
  response: DocumentationResponse,
): DocumentationMeta => ({
  page: Number(response.meta?.page) || 1,
  size: Number(response.meta?.size) || 20,
  total: Number(response.meta?.total) || 0,
  totalPages: Number(response.meta?.totalPages) || 0,
});

export const documentationQuery = (
  filters: DocumentationFilters,
  sort: "documentation_recent" | null = "documentation_recent",
): string => {
  const query = new URLSearchParams({
    page: String(filters.page),
    size: String(filters.size),
  });
  if (filters.year) query.set("year", filters.year);
  if (filters.activitySlug) query.set("activitySlug", filters.activitySlug);
  if (sort) query.set("sort", sort);
  return query.toString();
};

export const formatDocumentationDate = (value: string): string => {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("id-ID", { dateStyle: "long" }).format(date);
};
